import React from "react";

import { logger } from "~/lib/logger";

// AIDEV-NOTE: The parts map (parts-force-graph-inner-d3) builds SVG imperatively and runs d3-force.
// A malformed graph (e.g. an edge whose endpoint node was filtered out) makes forceLink throw and
// takes the whole map — and its parent subtree — down with it. Before this boundary existed the only
// signal was a thumbs-down on the feedback form, so render failures were invisible in Sentry/Datadog.
// PHI: never log part names or relationship descriptions here — counts and ids only.

interface PartsMapErrorBoundaryState {
    hasError: boolean;
}

interface PartsMapErrorBoundaryProps {
    children: React.ReactNode;
    /** Where the map is rendered — e.g. "session-notes" | "part-detail". Aids triage. */
    surface: string;
    /** Graph shape at render time; counts only, no names. */
    nodeCount?: number;
    edgeCount?: number;
}

export class PartsMapErrorBoundary extends React.Component<
    PartsMapErrorBoundaryProps,
    PartsMapErrorBoundaryState
> {
    constructor(props: PartsMapErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): PartsMapErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const { surface, nodeCount, edgeCount } = this.props;
        logger
            .withError(error)
            .withMetadata({
                surface,
                nodeCount,
                edgeCount,
                componentStack: errorInfo.componentStack,
                event: "parts_map_render_failed",
            })
            .error("Parts map failed to render");
    }

    render() {
        if (this.state.hasError) {
            // AIDEV-NOTE: Degrade quietly — the map is supplementary to the notes/part page around
            // it, so a loud error block would be more disruptive than the missing map.
            return (
                <div className="rounded-lg border border-brand-forest-200 bg-white p-4 text-sm text-muted-foreground">
                    This map couldn&rsquo;t be displayed. The rest of your
                    session notes are unaffected.
                </div>
            );
        }

        return this.props.children;
    }
}
